import { Injectable } from "@angular/core";
import LoggerService from "@bfx/logger";
import { GammaUtils } from "app/bfx-blotter-orders/GammaUtils";
import { OEGammaLegType } from "app/order-entry/data/enums/oe-gamma-leg-type";
import { OEDataModel } from "app/order-entry/data/models/oe-data.model";
import { OEPanelGammaLeg } from "app/order-entry/data/models/oe-panel-gamma-leg.model";
import { OEPanelGammaViewModel } from "app/order-entry/data/models/oe-panel-gamma-view.model";
import {
  OrderDataImpl,
  BigNumber,
  StrategyType,
  MarkUpType,
  CurrencyPairImpl,
  OrderData,
} from "shared/cubiq-model-multi-asset";
import { CcyPairLookupService, CcyPairPromiseLookupService } from "shared/cubiq-services";

const logger = LoggerService.for(__dirname);

@Injectable()
export class OEAdapterGammaHelper {
  constructor(protected currencyPairService: CcyPairPromiseLookupService) {
    // empty constructor
  }

  /**
   *
   * @param model
   * @param order
   */
  public parseGammaOrder(model: OEDataModel, order: OrderDataImpl): void {
    if (order.strategyType === StrategyType.GAMMA) {
      model.rungsAbove = this.getRungsAbove(order);
      model.rungsBelow = this.getRungsBelow(order);

      let delta: BigNumber = this.getFixedDelta(order);
      model.delta = delta != null ? delta.toFixed() : null;
      model.orderAmount = this.getFixedAmount(order);
      model.gammaModel = this.loadGammaViewModel(order);
      model.requestPrice = this.getGammaMidPrice(order);
      model.strategy.strategyType = StrategyType.GAMMA;
    }
  }

  /**
   * Get number of rungs above mid
   * @param order the order to be checked
   * @returns number of rungs above, null if it's not gamma
   */
  public getRungsAbove(order: OrderDataImpl): number {
    let above: number = null;

    if (order.strategyType === StrategyType.GAMMA) {
      above = 0;

      let rungs = GammaUtils.getAllRungs(order);
      let limit = GammaUtils.getOtherSideOfGamma(rungs, order);

      rungs.forEach((r) => {
        if (r.gammaRungId.lt(limit.gammaRungId)) {
          above++;
        }
      });
    }

    return above;
  }

  /**
   * Get number of rungs below mid
   * @param order the order to be checked
   * @returns number of rungs below, null if it's not gamma
   */
  public getRungsBelow(order: OrderDataImpl): number {
    let below: number = null;

    if (order.strategyType === StrategyType.GAMMA) {
      below = 0;

      let rungs = GammaUtils.getAllRungs(order);
      let limit = GammaUtils.getOtherSideOfGamma(rungs, order);

      rungs.forEach((r) => {
        if (r.gammaRungId.gt(limit.gammaRungId)) {
          below++;
        }
      });
    }

    return below;
  }

  /**
   * Checks if all legs of a gamma strategy are distanced on a fixed delta
   * @param order a gamma leg who's siblings need to be checked
   * @returns the fixed delta or null if the legs are not on a fixed delta
   * or this is not a gamma strategy
   */
  public getFixedDelta(order: OrderDataImpl): BigNumber {
    let delta: BigNumber = null;

    if (order.strategyType === StrategyType.GAMMA) {
      let rungs = GammaUtils.getAllRungs(order);

      if (Array.isArray(rungs) && rungs.length > 1) {
        rungs.sort((r1, r2) => r1.requestRt.comparedTo(r2.requestRt));

        for (let i: number = 1; i < rungs.length; i++) {
          let d1 = rungs[i].requestRt.minus(rungs[i - 1].requestRt);
          if (delta == null) {
            delta = d1;
          } else if (d1.comparedTo(delta) != 0) {
            delta = null;
            break;
          }
        }
      }
    }

    if (delta != null) {
      let inverted: boolean = order.isInvertedOrderFg;
      let lookup: CcyPairLookupService = this.currencyPairService.getBase();
      let pair: CurrencyPairImpl = lookup.getCcyPair(order.baseCode, order.nonBaseCode);

      if (pair != null) {
        let pips: number = inverted ? pair.spotInvertDecimalsQty : pair.pipsQty;
        delta = new BigNumber("10").pow(pips).times(delta);
      } else {
        logger.out.warn(
          `${this.constructor.name}: Failed to convert from decimals to pips ` +
            `for delta due to pair not found ${order.baseCode}${order.nonBaseCode}`,
        );
      }
    }

    return delta;
  }

  /**
   * Checks if all legs of a gamma strategy have the same amount
   * @param order a gamma leg who's siblings need to be checked
   * @returns the amount if all legs have the same amount, null if they are not
   * or this is not a gamma strategy
   */
  public getFixedAmount(order: OrderDataImpl): BigNumber {
    let amount: BigNumber = null;

    if (order.strategyType === StrategyType.GAMMA) {
      let rungs = GammaUtils.getAllRungs(order);
      let mid = GammaUtils.getMidRungOfGamma(rungs);

      if (null != mid) {
        rungs = rungs.filter((r) => r.gammaRungId.neq(mid.gammaRungId));
      }

      if (Array.isArray(rungs) && rungs.length > 0) {
        let amt: BigNumber = rungs[0].orderQty;
        if (rungs.every((r) => r.orderQty != null && r.orderQty.comparedTo(amt) == 0)) {
          amount = amt;
        }
      }
    }

    return amount;
  }

  /**
   * Determine if all legs in a gamma strategy have the same entry markup
   * @param order the order part of gamma strategy to be checked
   * @returns true if all legs have the same entry markup, false otherwise or if
   * the order is not gamma
   */
  public hasSameMarkup(order: OrderDataImpl): boolean {
    let sameMarkup: boolean = false;

    if (order.strategyType === StrategyType.GAMMA) {
      let rungs = GammaUtils.getAllRungs(order);
      let mid = GammaUtils.getMidRungOfGamma(rungs);

      if (null != mid) {
        rungs = rungs.filter((r) => r.gammaRungId.neq(mid.gammaRungId));
      }

      if (Array.isArray(rungs) && rungs.length > 1) {
        let markup: BigNumber = rungs[0].spotMarkupValue;
        let markupType: MarkUpType = rungs[0].spotMarkupType;

        sameMarkup = rungs.every((o) => {
          return this.isSame(markup, o.spotMarkupValue) && markupType === o.spotMarkupType;
        });
      }
    }

    return sameMarkup;
  }

  private isSame(a: BigNumber, b: BigNumber): boolean {
    return (a == null && b == null) || (a != null && b != null && a.comparedTo(b) == 0);
  }

  /**
   *
   * @param order
   * @returns
   */
  public loadGammaViewModel(order: OrderDataImpl): OEPanelGammaViewModel {
    let model: OEPanelGammaViewModel = new OEPanelGammaViewModel();
    this.loadOrderData(model, order);
    return model;
  }

  /**
   * Convert gamma order data to gamma viewer model by populating the given
   * gamma view model
   * @param model the gamma viewer model to populate
   * @param order the gamma strategy to populate it from
   */
  public loadOrderData(model: OEPanelGammaViewModel, order: OrderDataImpl): void {
    let rungs: OrderDataImpl[] = GammaUtils.getAllRungs(order);
    let limit = GammaUtils.getOtherSideOfGamma(rungs, order);

    rungs.sort((r) => r.gammaRungId.compare(r.gammaRungId));
    let prev: OEPanelGammaLeg = null;
    let legsAbove: OEPanelGammaLeg[] = [];
    let legsBelow: OEPanelGammaLeg[] = [];

    rungs.forEach((r) => {
      let above: boolean = r.gammaRungId.lt(limit.gammaRungId);
      let leg: OEPanelGammaLeg = this.loadGammaLeg(r);

      if (prev != null) {
        leg.aboveLeg = prev;
        prev.belowLeg = leg;
      }

      prev = leg;

      if (limit !== r) {
        leg.legType = above ? OEGammaLegType.ABOVE : OEGammaLegType.BELOW;
        let legs: OEPanelGammaLeg[] = above ? legsAbove : legsBelow;
        legs.push(leg);
      } else {
        model.gammaMedLeg = leg;
        leg.legType = OEGammaLegType.MID;
      }
    });

    // Align leg IDs
    legsAbove
      .slice()
      .reverse()
      .forEach((r, i) => {
        r.legId = i + 1;
      });

    legsBelow.forEach((r, i) => {
      r.legId = -i - 1;
    });

    model.gammaLegsAbove = legsAbove;
    model.gammaLegsBelow = legsBelow;
    model.rungsAbove = legsAbove.length;
    model.rungsBelow = legsBelow.length;
  }

  protected loadGammaLeg(order: OrderDataImpl): OEPanelGammaLeg {
    let leg: OEPanelGammaLeg = new OEPanelGammaLeg();

    leg.amount = order.orderQty;
    leg.direction = order.clientIntent;
    leg.rate = order.requestRt;
    leg.extRef = order.clOrdID;
    leg.fixedCcy = order.fixedCode;

    return leg;
  }

  protected getGammaMidPrice(order: OrderData): BigNumber {
    let rungs: OrderDataImpl[] = GammaUtils.getAllRungs(order);
    let mid = GammaUtils.getMidRungOfGamma(rungs);
    return mid.requestRt;
  }
}
